import { useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { Camera, Trash2, User, Clock } from "lucide-react";
import { AccountLayout } from "@/views/account/AccountLayout";
import { Card } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Field } from "@/components/Field";
import { FormSection, FormFields, FormRow } from "@/components/FormSection";
import { SectionSaveBar } from "@/components/SectionSaveBar";

interface Account {
  name: string;
  email: string;
  provider: string | null;
  lastLoginAt: string | null;
  avatarUrl: string | null;
  memberSince: string | null;
}

interface Props {
  account: Account;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function AccountProfile({ account }: Props) {
  const initial = { name: account.name, email: account.email };
  const [form, setForm] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const dirty = form.name !== saved.name || form.email !== saved.email;

  const fileInput = useRef<HTMLInputElement>(null);

  function save(): Promise<void> {
    return new Promise((resolve) => {
      router.patch(
        "/account",
        { user: { name: form.name, email: form.email } },
        {
          preserveScroll: true,
          onSuccess: () => setSaved(form),
          onFinish: () => resolve(),
        },
      );
    });
  }

  function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Inertia sends multipart automatically when it detects a File.
    router.patch("/account/update_avatar", { avatar: file }, { preserveScroll: true });
    e.target.value = "";
  }

  function removeAvatar() {
    router.delete("/account/remove_avatar", { preserveScroll: true });
  }

  return (
    <AccountLayout title="Profile">
      {/* Avatar */}
      <Card className="mb-6">
        <Card.Body className="flex items-center gap-4">
          {account.avatarUrl ? (
            <img
              src={account.avatarUrl}
              alt=""
              className="size-16 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent-text [&_svg]:size-7">
              <User />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-fg">Profile photo</div>
            <div className="text-xs text-fg-muted">PNG or JPG, up to a few MB.</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onAvatarChange}
            />
            <Button intent="secondary" size="sm" onClick={() => fileInput.current?.click()}>
              <Camera /> Change
            </Button>
            {account.avatarUrl && (
              <Button intent="ghost" size="sm" onClick={removeAvatar} aria-label="Remove photo">
                <Trash2 />
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Profile details */}
      <FormSection id="profile" title="Profile" description="Your name and contact email.">
        <FormFields>
          <FormRow label="Name" htmlFor="account-name">
            <Field.Root>
              <Field.Input
                id="account-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Your name"
              />
            </Field.Root>
          </FormRow>
          <FormRow label="Email" htmlFor="account-email" helper="Used to sign in and for statement notifications.">
            <Field.Root>
              <Field.Input
                id="account-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="you@example.com"
              />
            </Field.Root>
          </FormRow>
          <FormRow label="Member since">
            <span className="text-sm text-fg-muted tabular">{fmtDate(account.memberSince)}</span>
          </FormRow>
        </FormFields>
      </FormSection>

      <Card className="mt-6">
        <Card.Body className="flex items-start gap-3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-fg-muted [&_svg]:size-3.5">
            <Clock />
          </span>
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium text-fg">Last sign-in</div>
            <div className="mt-0.5 text-fg-muted tabular">
              {account.lastLoginAt
                ? new Date(account.lastLoginAt).toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "Never"}
            </div>
          </div>
          {account.provider && (
            <Badge tone="neutral" className="capitalize">{account.provider}</Badge>
          )}
        </Card.Body>
      </Card>

      <SectionSaveBar dirty={dirty} onSave={save} onDiscard={() => setForm(saved)} />
    </AccountLayout>
  );
}
